<template>
  <ul class="tree" :class="{ tree_disabled: disabled }">
    <li
      v-for="(item, key) in items"
      :key="key"
      class="item"
      :class="{ item_with_no_children: !item.children }"
    >
      <font-awesome-icon
        v-if="!item.children && displayChildIcon"
        :icon="['far', 'clipboard-list']"
        class="icon_questionnaire"
        fixed-width
      />
      <button
        @click="onClick(item)"
        type="button"
        class="content"
        :class="{
          'content--regular': !item.children,
          'content--bold': !!item.children,
          'content--active': !!item.isActive,
          'content--disabled': disabled,
        }"
      >
        <span v-if="item.children" class="icon">
          <font-awesome-icon
            v-if="item.isOpen"
            :icon="['far', 'chevron-down']"
            fixed-width
          />
          <font-awesome-icon
            v-else
            :icon="['far', 'chevron-right']"
            fixed-width
          />
        </span>
        <span
          v-if="!item.children && item.id"
          class="item-id"
          v-tippy="{ content: tooltipTitle }"
        >
          {{
            item.id.startsWith("riq_") ? item.id.replace(/riq_/, "") : item.id
          }}
        </span>
        <span>{{ item.name }}</span>
      </button>
      <TreeView
        v-if="item.children && item.isOpen"
        :items="item.children"
        :onClickHandler="onClickHandler"
        :tooltipTitle="tooltipTitle"
      />
    </li>
  </ul>
</template>

<script>
import {
  faChevronDown,
  faChevronRight,
  faClipboardList,
} from "@fortawesome/pro-regular-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";

library.add(faChevronDown, faChevronRight, faClipboardList);

export default {
  name: "TreeView",
  components: { FontAwesomeIcon },
  props: {
    /**
     * @vuese
     * Expecting the entire Tree to be passed.
     * A tree consists of a single array.
     * The array is expected to carry items.
     * The items should take the following shape:
     * {
     *   name: String, // the name of the item
     *   children: Array, // an item can carry children which asume the same shape as this described object
     *   isOpen: Boolean, // state to indicate to have the children shown. This state is mutated from inside the component onClick.
     *   isActive: Boolean, // state to color the name blue. Intended for items without children.
     * }
     *
     * @example usage
     *
     * <TreeView :items="[
     *    {
     *        name: 'Futurama International',
     *        isOpen: true,
     *        children: [
     *            {
     *                name: 'Planet Express',
     *                isOpen: false,
     *                children: [
     *                    { name: 'Foo' },
     *                    { name: 'Bar'},
     *                ],
     *            },
     *            {
     *                name: 'Characters',
     *                isOpen: false,
     *                children: [
     *                    { name: 'Philip J. Fry' },
     *                    { name: 'Leela Turanga', isActive: true },
     *                    { name: 'Bender' },
     *                    { name: 'Amy Wong' },
     *                    { name: 'Dr. Zoidberg' },
     *                    { name: 'Hubert J. Fransworth' },
     *                    { name: 'Zapp Brannigan' },
     *                ],
     *            },
     *        ],
     *      },
     *    ]" />
     */
    items: {
      type: Array,
      required: true,
    },
    /**
     * The callback function that will be invoked when a click event happens
     * It passes one argument; the treeview item object.
     */
    onClickHandler: {
      type: Function,
      required: true,
    },
    disabled: {
      type: Boolean,
    },
    displayChildIcon: {
      type: Boolean,
      default: true,
    },
    tooltipTitle: {
      type: String,
      default: "Item ID",
    },
  },
  methods: {
    onClick(item) {
      this.onClickHandler(item);
      if (typeof item.isOpen === "undefined") return;
      // eslint-disable-next-line no-param-reassign
      item.isOpen = !item.isOpen;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../style_variables/style_variables.scss";

.tree {
  list-style: none;
  margin: 0;
  padding: 0 0 0 24px;
}
.tree_disabled {
  pointer-events: none;
  background-color: $grey-bg;
  // !important is an exceptional, somewhat quick and dirty fix
  // the following property implies that this class is always coupled with .questionnaire_block, which would otherwise override the border colour
  border-color: $grey_mischka !important;
  color: $grey_mischka;
}

.item {
  margin: 16px 0;
  word-break: break-word;

  :last-of-type {
    margin-bottom: 0;
  }
}

.item_with_no_children {
  display: flex;
  align-items: baseline;
  margin: 8px 0;
}

.content {
  cursor: pointer;
  display: inline;
  margin: 0;
  background: none;
  border: none;
  padding: 4px 0;
  width: 100%;
  text-align: start;
  font-family: "Open Sans", sans-serif;
  font-size: 0.875rem;
  display: flex;
  align-items: center;

  &.content--regular {
    color: $grey-mid;
    padding-left: 10px;
  }

  &.content--bold {
    font-weight: 600;
    color: $black;
  }

  &.content--active {
    color: $blue;
    padding-left: 10px;
  }

  &.content--disabled {
    font-weight: 600;
    color: $grey_mischka;
  }
}

.icon {
  margin-right: 12px;
  width: 16px;
  height: 16px;
  font-size: 12px;
}

.icon_questionnaire {
  width: 14px;
  height: 14px;
  font-size: 15px;
}
.item-id {
  color: $black;
  font-size: 14px;
  background-color: $grey_alabaster;
  border-radius: 4px;
  display: inline-block;
  padding: 2px 5px;
  margin-right: 8px;
}
</style>
